import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import { InvoiceProjectCustomer, ItemizedInvoice } from './invoiceType';
import { formatAddress, formatCurrency } from '../lib/global_function';
import InvoiceApi from './invoiceApi';
import QuickbooksApi from '../quickbooksConnection/quickbooksApi';
import { getAccountIdFromSessionStorage } from '../lib/global_function';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Spinner from '../components/ui/spinner';

interface InvoiceTotals {
  sub_total: number;
  tax_rate: number;
  tax_amount: number;
  discount_usd: number;
  amount_paid: number;
  balance: number;
}

const InvoiceToQuickbooks: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Invoice', path: '/main/project/invoice' },
    { label: 'Send to Quickbooks', path: '/main/project/invoice/toquickbooks' },
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const invoiceProjectCustomer: InvoiceProjectCustomer | undefined = location.state?.invoice;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [itemizedInvoices, setItemizedInvoices] = useState<ItemizedInvoice[]>([]);
  const [invoiceTotals, setInvoiceTotals] = useState<InvoiceTotals | null>(null);
  const [isQuickBooksConnected, setIsQuickBooksConnected] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);
  const [quickbooksPopup, setQuickbooksPopup] = useState<Window | null>(null);

  // Get contractor's account ID from session storage
  const accountId = getAccountIdFromSessionStorage(process.env.REACT_APP_SESSION_STORAGE_KEY as string);

  // Check QuickBooks connection status
  const checkQuickBooksConnection = async () => {
    if (!accountId) {
      console.error('Account ID not found in session storage');
      return;
    }

    setIsCheckingConnection(true);
    try {
      const response = await QuickbooksApi.checkConnectionStatus(accountId);
      setIsQuickBooksConnected(response.connected);
    } catch (error) {
      console.error('Failed to check QuickBooks connection:', error);
      setIsQuickBooksConnected(false);
    } finally {
      setIsCheckingConnection(false);
    }
  };

  // Listen for popup messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'QUICKBOOKS_OAUTH_SUCCESS') {
        // Close popup if it exists
        if (quickbooksPopup) {
          quickbooksPopup.close();
          setQuickbooksPopup(null);
        }

        // Refresh connection status after a short delay
        setTimeout(() => {
          checkQuickBooksConnection();
        }, 1000);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      // Clean up popup if component unmounts
      if (quickbooksPopup && !quickbooksPopup.closed) {
        quickbooksPopup.close();
      }
    };
  }, [quickbooksPopup]);

  // Monitor popup closure for cases where message fails
  useEffect(() => {
    if (!quickbooksPopup) return;

    const checkClosed = setInterval(() => {
      if (quickbooksPopup.closed) {
        clearInterval(checkClosed);
        setQuickbooksPopup(null);

        // Check connection status in case user completed auth but message failed
        setTimeout(() => {
          checkQuickBooksConnection();
        }, 1000);
      }
    }, 1000);

    return () => clearInterval(checkClosed);
  }, [quickbooksPopup]);

  // Fetch itemized invoices and check QB connection
  // Fetch itemized invoices and check QB connection
  useEffect(() => {
    if (!invoiceProjectCustomer) {
      setErrorMessage('Invoice not available, please select an invoice by clicking Go To Invoice button.');
      return;
    }

    if (!accountId) {
      setErrorMessage('Account ID not found. Please log in again.');
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch itemized invoices with totals - data is already flattened by the API function
        const response = await InvoiceApi.getItemizedInvoices(invoiceProjectCustomer.invoice_id);

        // Set the already-flattened line items
        setItemizedInvoices(response.itemizedInvoices);

        // Set totals
        setInvoiceTotals({
          sub_total: response.totals.sub_total,
          tax_rate: response.totals.tax_rate,
          tax_amount: response.totals.tax_amount,
          discount_usd: response.totals.discount_usd,
          amount_paid: response.totals.amount_paid,
          balance: response.totals.balance
        });

        // Check QuickBooks connection status
        await checkQuickBooksConnection();
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [invoiceProjectCustomer, accountId]);
  const handleConnectToQuickBooks = async () => {
    if (!accountId) {
      setErrorMessage('Account ID not found. Please log in again.');
      return;
    }

    setErrorMessage(null);

    try {
      // Build popup URL - point to the popup component route
      const popupUrl = `${window.location.origin}/quickbooks/callback`;

      // Open popup window
      const popup = window.open(
        popupUrl,
        'quickbooks-connect',
        'width=600,height=700,scrollbars=yes,resizable=yes,location=yes'
      );

      if (popup) {
        setQuickbooksPopup(popup);
      } else {
        setErrorMessage('Popup blocked. Please allow popups for this site and try again.');
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to open QuickBooks connection popup');
    }
  };

  const handleSendToQuickbooks = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsLoading(true);

    if (!invoiceProjectCustomer) {
      setErrorMessage('No invoice selected');
      setIsLoading(false);
      return;
    }

    if (!accountId) {
      setErrorMessage('Account ID not found. Please log in again.');
      setIsLoading(false);
      return;
    }

    // Check if customer_id exists
    if (!invoiceProjectCustomer.customer_id) {
      setErrorMessage('Customer ID not found in invoice data.');
      setIsLoading(false);
      return;
    }

    // Use totals from API response if available, otherwise fallback to invoiceProjectCustomer
    const subtotal = invoiceTotals?.sub_total ?? Number(invoiceProjectCustomer.sub_total_sum_lineitems);
    const taxRate = invoiceTotals?.tax_rate ?? Number(invoiceProjectCustomer.tax_rate);
    const taxAmount = invoiceTotals?.tax_amount ?? Number(invoiceProjectCustomer.tax_amount);
    const discountUsd = invoiceTotals?.discount_usd ?? Number(invoiceProjectCustomer.discount_usd || 0);
    const total = subtotal + taxAmount - discountUsd;

    try {
      // Prepare enhanced invoice data for QuickBooks
      const invoiceData = {
        customer_name: `${invoiceProjectCustomer.customer_first_name} ${invoiceProjectCustomer.customer_last_name}`,
        invoice_name: `MrJoe.pro Invoice #${invoiceProjectCustomer.invoice_id}`,
        invoice_number: invoiceProjectCustomer.invoice_id.toString(),
        subtotal: subtotal,
        tax_rate: taxRate,
        tax_amount: taxAmount,
        total: total,
        discount_usd: discountUsd,
        issue_date: invoiceProjectCustomer.issue_date,
        due_date: invoiceProjectCustomer.due_date,
        payment_instruction: invoiceProjectCustomer.payment_instruction,
        line_items: itemizedInvoices.map(item => ({
          description: item.description,
          code: item.code,
          amount: Number(item.amount_dollar)
        }))
      };

      // Send to QuickBooks using customer_id and accountId
      const response = await QuickbooksApi.sendInvoiceToQuickbooks(
        invoiceProjectCustomer.customer_id,
        invoiceData,
        accountId,
        invoiceProjectCustomer.invoice_id
      );

      // Show success message with QuickBooks details
      const successMsg = response.qb_invoice_number
        ? `Invoice #${response.qb_invoice_number} sent to QuickBooks successfully!`
        : 'Invoice sent to QuickBooks successfully!';

      setSuccessMessage(successMsg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error sending invoice to QuickBooks:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to send to QuickBooks';
      setErrorMessage(errorMsg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigateToInvoiceList = () => {
    navigate('/main/project/invoice');
  };

  const formatCustomerAddress = () =>
    invoiceProjectCustomer
      ? formatAddress(
        invoiceProjectCustomer.customer_address_1,
        invoiceProjectCustomer.customer_address_2,
        invoiceProjectCustomer.customer_city,
        invoiceProjectCustomer.customer_state,
        invoiceProjectCustomer.customer_zip
      )
      : '';

  // Use balance from API response if available, otherwise calculate from invoiceProjectCustomer
  const balanceDue = invoiceTotals?.balance ??
    (Number(invoiceProjectCustomer?.final_total || 0) - Number(invoiceProjectCustomer?.amount_paid || 0));

  const isPaid = balanceDue <= 0;

  // Get display values from API totals or fallback to invoiceProjectCustomer
  const displaySubtotal = invoiceTotals?.sub_total ?? Number(invoiceProjectCustomer?.sub_total_sum_lineitems || 0);
  const displayTaxRate = invoiceTotals?.tax_rate ?? Number(invoiceProjectCustomer?.tax_rate || 0);
  const displayTaxAmount = invoiceTotals?.tax_amount ?? Number(invoiceProjectCustomer?.tax_amount || 0);
  const displayDiscount = invoiceTotals?.discount_usd ?? Number(invoiceProjectCustomer?.discount_usd || 0);
  const displayAmountPaid = invoiceTotals?.amount_paid ?? Number(invoiceProjectCustomer?.amount_paid || 0);
  const displayTotal = displaySubtotal + displayTaxAmount - displayDiscount;

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <h1 className="text-2xl font-semibold text-foreground mb-6">Send Invoice to QuickBooks</h1>

          {/* Error Message */}
          {errorMessage && (
            <Alert variant="error" className="mb-6">
              {errorMessage}
            </Alert>
          )}

          {/* Success Message */}
          {successMessage && (
            <Alert variant="success" className="mb-6">
              {successMessage}
            </Alert>
          )}

          {invoiceProjectCustomer ? (
            <div className="space-y-6">
              {/* QuickBooks Connection Status Banner */}
              <div className={`p-4 rounded-lg border-2 ${isQuickBooksConnected
                  ? 'bg-green-50 border-green-300'
                  : 'bg-yellow-50 border-yellow-300'
                }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isQuickBooksConnected ? (
                      <CheckCircle className="size-6 text-green-600" />
                    ) : (
                      <AlertCircle className="size-6 text-yellow-600" />
                    )}
                    <div>
                      <span className={`font-semibold ${isQuickBooksConnected ? 'text-green-800' : 'text-yellow-800'
                        }`}>
                        QuickBooks Status:
                      </span>
                      <span className={`ml-2 ${isQuickBooksConnected ? 'text-green-700' : 'text-yellow-700'
                        }`}>
                        {isCheckingConnection ? 'Checking...' : (isQuickBooksConnected ? 'Connected ✓' : 'Not Connected')}
                      </span>
                    </div>
                  </div>

                  {!isCheckingConnection && !isQuickBooksConnected && (
                    <button
                      onClick={handleConnectToQuickBooks}
                      disabled={isLoading}
                    >
                      <img
                        src="/connect_to_qb_official.png"
                        alt="Connect to QuickBooks"

                      />

                    </button>
                  )}       
                </div>

                {quickbooksPopup && !quickbooksPopup.closed && (
                  <p className="mt-2 text-xs text-yellow-700">
                    QuickBooks authorization window is open. Please complete the authorization process.
                  </p>
                )}
              </div>

              {/* Customer Information */}
              <div className="p-6 bg-white border border-gray-200 rounded-lg">
                <h2 className="text-lg font-semibold text-foreground mb-4">Customer Information</h2>
                <div className="text-sm">
                  <span className="font-semibold text-gray-700">Name:</span>
                  <span className="ml-2 text-foreground">
                    {`${invoiceProjectCustomer.customer_first_name} ${invoiceProjectCustomer.customer_last_name}`}
                  </span>
                  <span className="mx-2">•</span>
                  <span className="font-semibold text-gray-700">Address:</span>
                  <span className="ml-2 text-foreground">{formatCustomerAddress()}</span>
                </div>
              </div>

              {/* Invoice Details */}
              <div className="p-6 bg-white border-2 border-primary rounded-lg">
                {/* Project Name */}
                <div className="mb-4">
                  <span className="font-semibold text-gray-700">Project Name:</span>
                  <span className="ml-2 text-foreground">
                    {invoiceProjectCustomer.project_name || `Project #${invoiceProjectCustomer.project_id}`}
                  </span>
                </div>

                {/* Invoice Summary */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-foreground">
                      Invoice #{invoiceProjectCustomer.invoice_id}
                    </h3>
                    <Badge variant={isPaid ? 'success' : 'error'}>
                      {isPaid ? 'PAID' : 'OUTSTANDING'}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                    <div>
                      <span className="font-semibold text-gray-700">Subtotal:</span>
                      <span className="ml-2 text-foreground">
                        ${formatCurrency(displaySubtotal.toFixed(2))}
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700">Tax Rate:</span>
                      <span className="ml-2 text-foreground">
                        {displayTaxRate.toFixed(2)}%
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700">Tax Amount:</span>
                      <span className="ml-2 text-foreground">
                        ${formatCurrency(displayTaxAmount.toFixed(2))}
                      </span>
                    </div>
                    {displayDiscount > 0 && (
                      <div>
                        <span className="font-semibold text-gray-700">Discount:</span>
                        <span className="ml-2 text-red-600 font-semibold">
                          -${formatCurrency(displayDiscount.toFixed(2))}
                        </span>
                      </div>
                    )}
                    <div>
                      <span className="font-semibold text-gray-700">Total:</span>
                      <span className="ml-2 font-bold text-primary">
                        ${formatCurrency(displayTotal.toFixed(2))}
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700">Paid:</span>
                      <span className="ml-2 font-semibold text-green-600">
                        ${formatCurrency(displayAmountPaid.toFixed(2))}
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700">Balance Due:</span>
                      <span className={`ml-2 font-bold ${isPaid ? 'text-green-600' : 'text-red-600'}`}>
                        ${formatCurrency(balanceDue.toFixed(2))}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Itemized Invoices Table */}
                {itemizedInvoices.length > 0 && (
                  <div className="border-2 border-gray-400 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-100 border-b-2 border-gray-400">
                            <th className="px-4 py-2 text-left font-semibold text-gray-700 w-1/5">Code</th>
                            <th className="px-4 py-2 text-left font-semibold text-gray-700 w-1/2">Description</th>
                            <th className="px-4 py-2 text-right font-semibold text-gray-700 w-3/10">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {itemizedInvoices.map((item, index) => (
                            <tr
                              key={item.itemized_invoice_id}
                              className={`border-b border-gray-300 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                            >
                              <td className="px-4 py-2 text-foreground">{item.code}</td>
                              <td className="px-4 py-2 text-foreground">{item.description}</td>
                              <td className="px-4 py-2 text-right font-semibold text-green-600">
                                ${formatCurrency(Number(item.amount_dollar).toFixed(2))}
                              </td>
                            </tr>
                          ))}
                        </tbody>

                        <tfoot>
                          <tr className="bg-green-50 border-t-2 border-gray-400">
                            <td className="px-4 py-2 font-bold text-foreground">Total:</td>
                            <td className="px-4 py-2"></td>
                            <td className="px-4 py-2 text-right font-bold text-green-600">
                              ${formatCurrency(displaySubtotal.toFixed(2))}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                {isQuickBooksConnected && (
                  <Button
                    size="medium"
                    onClick={handleSendToQuickbooks}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Spinner size="sm" fullScreen={false} className="size-5" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <ExternalLink size={18} />
                        Send to QuickBooks
                      </>
                    )}
                  </Button>
                )}
                <Button
                  size="medium"
                  variant="inverted"
                  onClick={handleNavigateToInvoiceList}
                >
                  Back to Invoice List
                </Button>
              </div>
            </div>
          ) : (
            !errorMessage && (
              <div className="p-6 bg-white border border-gray-200 rounded-lg">
                <p className="text-gray-600">No invoice data available to display.</p>
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
};

export default InvoiceToQuickbooks;